import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Activity, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";

const getCompletionRateColor = (rate: number) => {
  if (rate >= 90) return "text-green-600";
  if (rate >= 75) return "text-amber-600";
  return "text-red-600";
};

const getCompletionRateLabel = (rate: number) => {
  if (rate >= 90) return "Excellent";
  if (rate >= 75) return "Good";
  return "Needs Attention";
};

const getOverdueColor = (count: number) => {
  return count > 0 ? "text-red-600" : "text-green-600";
};

const getOverdueLabel = (count: number) => {
  return count > 0 ? "Attention" : "On Track";
};

export const ScheduleKeyStatsCard: React.FC<any> = ({
  data,
}) => {
  return (
    <Card className="border-blue-200">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600" />
          <CardTitle className="page-title">Schedule Stats</CardTitle>
        </div>
        <p className="page-subtitle">
          Performance indicators and compliance metrics
        </p>
      </CardHeader>
      <CardContent className="space-y-3 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <div className="flex item-center gap-2">
              <div>
                <span className="text-sm text-gray-600">Completion Rate</span>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Percentage of tasks completed vs total tasks due under
                      this schedule.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="text-right">
            <span
              className={`font-semibold ${getCompletionRateColor(
                data?.progressPercentage
              )}`}
            >
              {data?.progressPercentage}%
            </span>
            <span
              className={`text-sm ml-2 ${getCompletionRateColor(
                data?.progressPercentage
              )}`}
            >
              {getCompletionRateLabel(data?.progressPercentage)}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <div className="flex item-center gap-2">
              <div>
                <span className="text-sm text-gray-600">Overdue SOs</span>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Number of overdue service orders linked to this schedule.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="text-right">
            <span
              className={`font-semibold ${getOverdueColor(
                data?.overdueServiceOrders
              )}`}
            >
              {data?.overdueServiceOrders}
            </span>
            <span
              className={`text-sm ml-2 ${getOverdueColor(
                data?.overdueServiceOrders
              )}`}
            >
              {getOverdueLabel(data?.overdueServiceOrders)}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <div className="flex item-center gap-2">
              <div>
                <span className="text-sm text-gray-600">Avg. Cycle Time</span>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Average time taken to complete tasks in this schedule
                      (from start to closure).
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
          <span className="font-semibold">{data?.estimatedDuration} days</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <div className="flex item-center gap-2">
              <div>
                <span className="text-sm text-gray-600">Total Runs</span>
              </div>
              <div className="mt-1">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Total number of times this schedule has been executed
                      since creation.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
          <span className="font-semibold">{data?.executionHistory?.length}</span>
        </div>
      </CardContent>
    </Card>
  );
};
